/**
 * Presentational helpers for the rewards wallet.
 *
 * Keeps the points→₹ rule, the source/entry-type → human-friendly mapping, and
 * expiry math out of the components so the page/dialog stay lean.
 */
import type { RewardTransactionResponse } from '@/api/rewards';

/**
 * 1 reward point = ₹1 of service-fee discount. Confirmed by the redeem API
 * (redeemed_points 200 → discount_amount 200.0). If the backend ever changes
 * this, prefer the API's `discount_amount` over multiplying client-side.
 */
export const POINTS_PER_RUPEE = 1;

/** Backend default for `reward_max_redemption_pct`: 50% of the service fee. */
export const DEFAULT_MAX_REDEMPTION_PCT = 0.5;

/** Credits within this window surface an "expiring soon" nudge. */
export const EXPIRY_SOON_DAYS = 30;

/** Points → rupees of discount. */
export function pointsToInr(points: number): number {
  return points * POINTS_PER_RUPEE;
}

/** Format a point amount with Indian digit grouping, e.g. 1150 → "1,150". */
export function formatPoints(points: number | null | undefined): string {
  if (points == null || !Number.isFinite(points)) return '—';
  return Math.abs(points).toLocaleString('en-IN');
}

/** Is this a balance-increasing entry? Defaults to treating unknowns as debits. */
export function isCredit(entryType: string): boolean {
  return entryType.toLowerCase() === 'credit';
}

/**
 * Human-friendly label for a ledger `source`. Falls back to a Title-Cased
 * version of the raw source so a brand-new backend source still reads sensibly.
 */
export function sourceLabel(source: string): string {
  const map: Record<string, string> = {
    referral_referee:  'Signup referral bonus',
    referral_referrer: 'Referral reward',
    redemption:        'Redeemed on a service',
    redeem:            'Redeemed on a service',
    admin_credit:      'Bonus from FinminOS',
    admin_adjustment:  'Balance adjustment',
    expiry:            'Points expired',
    expired:           'Points expired',
  };
  return (
    map[source.toLowerCase()] ??
    source
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/**
 * Days until a credit expires, or null when there's no expiry / it's past.
 * `now` is injectable to keep this pure and testable.
 */
export function daysUntilExpiry(
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  const ms = expiry.getTime() - now.getTime();
  if (ms <= 0) return null;
  return Math.ceil(ms / 86_400_000);
}

/**
 * From a set of recent ledger rows, find the soonest-expiring credit balance
 * within `EXPIRY_SOON_DAYS`. Returns null when nothing is expiring soon.
 */
export function findExpiringSoon(
  transactions: RewardTransactionResponse[],
  now: Date = new Date(),
): { points: number; days: number } | null {
  let soonest: { points: number; days: number } | null = null;
  for (const tx of transactions) {
    if (!isCredit(tx.entry_type)) continue;
    const days = daysUntilExpiry(tx.expires_at, now);
    if (days == null || days > EXPIRY_SOON_DAYS) continue;
    if (!soonest || days < soonest.days) {
      soonest = { points: tx.points, days };
    }
  }
  return soonest;
}
